import { useState, useRef } from 'react'
import {
    MdPictureAsPdf,
    MdTableChart,
    MdFileDownload,
    MdEmail,
    MdClose,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import {
    ReportType,
    ReportFilters,
    exportReport,
    sendReportByEmail,
} from '@/lib/api/reports/reports-api'

interface ExportActionsProps {
    reportType: ReportType
    filters: ReportFilters
    /**
     * Colunas visíveis na tabela. O arquivo sai com elas — exportar as dezessete
     * colunas depois de esconder metade na tela era a queixa que originou isto.
     */
    columns?: string[]
    /** Exporta o resumo por item em vez da lista de atendimentos. */
    grouped?: boolean
    disabled?: boolean
}

export default function ExportActionsComponent({
    reportType,
    filters,
    columns,
    grouped = false,
    disabled = false,
}: ExportActionsProps) {
    const [exportingPdf, setExportingPdf] = useState(false)
    const [exportingXlsx, setExportingXlsx] = useState(false)
    const [exportingCsv, setExportingCsv] = useState(false)
    const [sendingEmail, setSendingEmail] = useState(false)
    const [email, setEmail] = useState('')
    const [emailFormat, setEmailFormat] = useState<'pdf' | 'xlsx' | 'csv'>(
        'pdf'
    )
    const modalRef = useRef<HTMLDialogElement>(null)

    // Sem recorte não manda nada: o backend trata "ausente" como todas as
    // colunas, e é o comportamento de sempre.
    const exportFilters: ReportFilters = {
        ...filters,
        ...(columns?.length ? { columns: columns.join(',') } : {}),
        // O arquivo sai no mesmo modo da tela.
        ...(grouped ? { grouped: 'true' } : {}),
    }

    const handleExport = async (format: 'pdf' | 'xlsx' | 'csv') => {
        const setLoading =
            format === 'pdf'
                ? setExportingPdf
                : format === 'xlsx'
                  ? setExportingXlsx
                  : setExportingCsv

        setLoading(true)
        try {
            await exportReport(reportType, format, exportFilters)
            toast.success('Relatório exportado com sucesso')
        } catch {
            toast.error('Erro ao exportar relatório')
        } finally {
            setLoading(false)
        }
    }

    const handleSendEmail = async () => {
        if (!email.trim()) {
            toast.error('Informe um e-mail válido')
            return
        }

        setSendingEmail(true)
        try {
            const response = await sendReportByEmail(
                reportType,
                email,
                emailFormat,
                exportFilters
            )
            if (response.success) {
                toast.success('Relatório enviado por e-mail com sucesso')
                setEmail('')
                modalRef.current?.close()
            } else {
                toast.error(response.error || 'Erro ao enviar relatório')
            }
        } catch {
            toast.error('Erro ao enviar relatório por e-mail')
        } finally {
            setSendingEmail(false)
        }
    }

    return (
        <>
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    className="btn btn-sm btn-outline gap-2"
                    disabled={disabled || exportingPdf}
                    onClick={() => handleExport('pdf')}
                >
                    {exportingPdf ? (
                        <span className="loading loading-spinner loading-xs" />
                    ) : (
                        <MdPictureAsPdf className="text-lg" />
                    )}
                    PDF
                </button>

                <button
                    type="button"
                    className="btn btn-sm btn-outline gap-2"
                    disabled={disabled || exportingXlsx}
                    onClick={() => handleExport('xlsx')}
                >
                    {exportingXlsx ? (
                        <span className="loading loading-spinner loading-xs" />
                    ) : (
                        <MdTableChart className="text-lg" />
                    )}
                    Excel
                </button>

                <button
                    type="button"
                    className="btn btn-sm btn-outline gap-2"
                    disabled={disabled || exportingCsv}
                    onClick={() => handleExport('csv')}
                >
                    {exportingCsv ? (
                        <span className="loading loading-spinner loading-xs" />
                    ) : (
                        <MdFileDownload className="text-lg" />
                    )}
                    CSV
                </button>

                <button
                    type="button"
                    className="btn btn-sm btn-outline gap-2"
                    disabled={disabled}
                    onClick={() => modalRef.current?.showModal()}
                >
                    <MdEmail className="text-lg" />
                    Enviar por E-mail
                </button>
            </div>

            <dialog ref={modalRef} className="modal">
                <div className="modal-box">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">
                            Enviar Relatório por E-mail
                        </h3>
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost btn-circle"
                            onClick={() => modalRef.current?.close()}
                        >
                            <MdClose className="text-xl" />
                        </button>
                    </div>

                    <div className="flex flex-col gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">E-mail</span>
                            </label>
                            <input
                                type="email"
                                className="input input-bordered w-full"
                                placeholder="exemplo@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Formato</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={emailFormat}
                                onChange={(e) =>
                                    setEmailFormat(
                                        e.target.value as
                                            | 'pdf'
                                            | 'xlsx'
                                            | 'csv'
                                    )
                                }
                            >
                                <option value="pdf">PDF</option>
                                <option value="xlsx">Excel</option>
                                <option value="csv">CSV</option>
                            </select>
                        </div>
                    </div>

                    <div className="modal-action">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => modalRef.current?.close()}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary gap-2"
                            disabled={sendingEmail}
                            onClick={handleSendEmail}
                        >
                            {sendingEmail && (
                                <span className="loading loading-spinner loading-xs" />
                            )}
                            Enviar
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button type="submit">fechar</button>
                </form>
            </dialog>
        </>
    )
}
